import type {
	FieldType,
	PersistedDynamicFieldDefinition,
} from "@/lib/dynamicForms/interfaces/dynamicForm.interface";
import {
	type LayoutElement,
	LayoutTypes,
} from "@/lib/layoutRenderer/schemas/layout.schema";

/**
 * Walks a form layout tree and collects `layoutInfo.fieldType` for each `Field` node.
 * Keys are `fieldDefinitionId` values from the layout (UUID or name).
 *
 * @param layout - Root layout element (typically `VerticalLayout`).
 * @returns Map from `fieldDefinitionId` to the declared UI field type.
 */
export function buildLayoutFieldTypeIndex(
	layout: LayoutElement,
): Map<string, FieldType> {
	const map = new Map<string, FieldType>();

	function walk(node: LayoutElement): void {
		if (node.type === LayoutTypes.Field) {
			const ft = node.layoutInfo?.fieldType;
			if (ft !== undefined) {
				map.set(node.fieldDefinitionId, ft);
			}
			return;
		}
		if ("elements" in node) {
			for (const child of node.elements) {
				walk(child);
			}
		}
	}

	walk(layout);
	return map;
}

/**
 * Resolves `layoutInfo.fieldType` for a persisted definition using the layout index.
 * Tries `def.id` then `def.name` because layout JSON may reference either.
 *
 * @param index - From {@link buildLayoutFieldTypeIndex}.
 * @param def - Field definition row.
 */
export function resolveLayoutFieldTypeForDefinition(
	index: Map<string, FieldType>,
	def: Pick<PersistedDynamicFieldDefinition, "id" | "name">,
): FieldType | undefined {
	return index.get(def.id) ?? index.get(def.name);
}
